import {useDispatch, useSelector} from 'react-redux';
import {AnyAction} from 'redux';
import {useSaveRunfile} from '../../../../../hooks/useSaveRunfile';
import {IElementSequenceQuestion} from '../../../../../incase-domain/elements/question/questionSequence';
import {IGameConfig} from '../../../../../incase-domain/proptypes/gameConfig';
import {SequenceQuestionInteraction} from '../../../../../incase-domain/proptypes/runfile';
import {getNewPoints} from '../../../../../service/gamification.service';
import {handleElementsClickedAction} from '../../../../../store/actions/chalkBoardElements.actions';
import {gameConfigSelector, loadedPresentationSelector} from '../../../../../store/selectors/presentation.selectors';
import {pointsSelector} from '../../../../../store/selectors/runfile.selectors';
import {isQuestionRetriable, isQuestionRetryLimitReached} from '../../../../../util/util';
import {useQuestionRemainingToast} from '../common/useQuestionRemainingToast';

function useSequenceQuestionTry(): (element: IElementSequenceQuestion, elementInteractions: SequenceQuestionInteraction) => void {
    const dispatch = useDispatch();

    const {emitRemainingTriesToast} = useQuestionRemainingToast();
    const saveRunfile = useSaveRunfile();

    const points = useSelector(pointsSelector);
    const gameConfig = useSelector(gameConfigSelector);
    const presentation = useSelector(loadedPresentationSelector);

    function isCorrectSequence(correctSequence: string[], answer: {text: string; comment: string}[]): boolean {
        return JSON.stringify(correctSequence) === JSON.stringify(answer.map((answer) => answer.text));
    }

    function handleTry(element: IElementSequenceQuestion, elementInteractions: SequenceQuestionInteraction): void {
        if (!element) return;

        const {correctSequence} = element;
        const answerArray = elementInteractions.markedAnswers || [];
        const {answered} = elementInteractions;

        if (answered) {
            return;
        }

        const isCorrect = isCorrectSequence(correctSequence, answerArray);

        let newPoints = points || 0;
        newPoints = getNewPoints(
            newPoints,
            isCorrectSequence(correctSequence, answerArray),
            gameConfig || ({} as IGameConfig),
            element.gameConfig
        );

        if (
            !isQuestionRetriable(element, presentation) ||
            isQuestionRetryLimitReached(elementInteractions, element, presentation, answered, isCorrect) ||
            element.isDebatable
        ) {
            const newPoints = getNewPoints(
                points || 0,
                element.isDebatable ? 'debatable' : 'correct',
                gameConfig || ({} as IGameConfig),
                element.gameConfig
            );

            saveRunfile(
                {
                    answered: true,
                    answeredSequence: answerArray,
                    retriedAnswers: elementInteractions.retriedAnswers || [],
                },
                element.id,
                element.slideId,
                newPoints || 0
            );
        } else {
            emitRemainingTriesToast(element, elementInteractions);
            saveRunfile(
                {
                    ...elementInteractions,
                    retriedAnswers: [...(elementInteractions.retriedAnswers || []), {attempt: answerArray}],
                    isWrong: true,
                },
                element.id,
                element.slideId,
                newPoints || 0
            );
        }
        const handleElementsClickedActionCreator = (): AnyAction => handleElementsClickedAction([element.id], !!element.clearAfterClicked);
        dispatch(handleElementsClickedActionCreator());
    }

    return handleTry;
}

export default useSequenceQuestionTry;
